// CSV import: an RFC 4180 parser (quoted fields, "" escapes, CRLF or LF, a leading BOM, comma or
// semicolon separated) and a header auto-mapper for the import preview.
export const MAX_ROWS = 500;
export const MAX_TEXT = 3000;

function separatorOf(text) {
  const firstLine = [];
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') quoted = !quoted;
    else if (!quoted && (c === '\n' || c === '\r')) break;
    firstLine.push(quoted ? '' : c);
  }
  const line = firstLine.join('');
  const commas = (line.match(/,/g) || []).length;
  const semicolons = (line.match(/;/g) || []).length;
  return semicolons > commas ? ';' : ',';
}

export function parseCsv(input) {
  const text = String(input || '').replace(/^﻿/, '');
  const separator = separatorOf(text);
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  let started = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"' && !started) { quoted = true; started = true; continue; }
    if (c === separator) { row.push(field); field = ''; started = false; continue; }
    if (c === '\r' || c === '\n') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      started = false;
      continue;
    }
    field += c;
    started = true;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  return rows.filter(cells => cells.some(cell => String(cell).trim() !== ''));
}

const FIELDS = {
  text: ['text', 'testimonial', 'quote', 'review', 'feedback', 'comment', 'comments', 'message', 'content', 'body'],
  name: ['name', 'full name', 'author', 'customer', 'customer name', 'reviewer', 'client', 'client name'],
  job_title: ['title', 'job title', 'role', 'position', 'job'],
  company: ['company', 'company name', 'organisation', 'organization', 'business'],
  rating: ['rating', 'stars', 'score'],
  date: ['date', 'created', 'created at', 'submitted', 'submitted at', 'review date', 'when'],
  source_label: ['source', 'from', 'platform', 'where']
};
export const FIELD_LABELS = { text: 'Testimonial text', name: 'Name', job_title: 'Job title', company: 'Company',
  rating: 'Rating', date: 'Date', source_label: 'Source' };

function normal(header) {
  return String(header || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

// {field: column index or -1}, matching exact names first and then names that contain a keyword.
export function mapHeaders(headers) {
  const names = headers.map(normal);
  const mapping = {};
  const used = new Set();
  Object.keys(FIELDS).forEach(field => {
    let index = names.findIndex((name, i) => !used.has(i) && FIELDS[field].indexOf(name) >= 0);
    if (index < 0) index = names.findIndex((name, i) => !used.has(i) && FIELDS[field].some(word => word.length > 3 && name.indexOf(word) >= 0));
    mapping[field] = index;
    if (index >= 0) used.add(index);
  });
  return mapping;
}

export function looksLikeHeader(cells) {
  const mapping = mapHeaders(cells);
  return mapping.text >= 0 || mapping.name >= 0;
}

// "5", "4/5", "★★★★" -> 1..5, "" -> null, anything else -> undefined (an error).
export function parseRating(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const stars = (text.match(/★/g) || []).length;
  if (stars) return stars >= 1 && stars <= 5 ? stars : undefined;
  const match = text.match(/^(\d(?:[.,]\d+)?)(?:\s*\/\s*5)?$/);
  if (!match) return undefined;
  const number = Math.round(parseFloat(match[1].replace(',', '.')));
  return number >= 1 && number <= 5 ? number : undefined;
}

function pad(number) { return String(number).padStart(2, '0'); }

// ISO dates stay; 14/03/2026 is read day first unless the second part can only be a day.
export function parseDate(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  let year; let month; let day;
  let match = text.match(/^(\d{4})[-/.](\d{1,2})(?:[-/.](\d{1,2}))?/);
  if (match) {
    year = +match[1]; month = +match[2]; day = match[3] ? +match[3] : 1;
  } else {
    match = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (!match) return undefined;
    const a = +match[1]; const b = +match[2];
    year = +match[3];
    if (b > 12 && a <= 12) { month = a; day = b; } else { day = a; month = b; }
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day || year < 1990 || date.getTime() > Date.now()) return undefined;
  return year + '-' + pad(month) + '-' + pad(day);
}

// Rows ready for POST /brands/{id}/imports plus a list of {row, message} problems.
export function toImportRows(table, mapping, hasHeader) {
  const body = hasHeader ? table.slice(1) : table;
  const rows = [];
  const problems = [];
  body.slice(0, MAX_ROWS).forEach((cells, index) => {
    const number = index + (hasHeader ? 2 : 1);
    const cell = field => (mapping[field] >= 0 ? String(cells[mapping[field]] || '').trim() : '');
    const text = cell('text');
    if (!text) { problems.push({ row: number, message: 'No testimonial text.' }); return; }
    if (text.length > MAX_TEXT) { problems.push({ row: number, message: 'The text is longer than 3,000 characters.' }); return; }
    const rating = parseRating(cell('rating'));
    if (rating === undefined) { problems.push({ row: number, message: 'The rating is not a number from 1 to 5.' }); return; }
    const date = parseDate(cell('date'));
    if (date === undefined) { problems.push({ row: number, message: 'The date is not a date like 2026-03-14.' }); return; }
    rows.push({ text, name: cell('name').slice(0, 120) || null, job_title: cell('job_title').slice(0, 120) || null,
      company: cell('company').slice(0, 120) || null, rating, date, source_label: cell('source_label').slice(0, 160) || null });
  });
  if (body.length > MAX_ROWS) problems.push({ row: MAX_ROWS + (hasHeader ? 2 : 1), message: 'Only the first 500 rows are imported at a time.' });
  return { rows, problems };
}
